import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Spinner from "../../components/Spinner/Spinner.tsx";
import { useDispatch, useSelector } from "react-redux";
import { fetchProperties } from "../../redux/propertiesSlice";
import { RootState, AppDispatch } from "../../redux/store";
import { Property as PropertyApi } from "../../types/property";
import AreaGuideTeaser from "src/components/AreaGuide/AreaGuideTeaser.tsx";
import { TenantGuides } from "./RentPage.tsx";
import AboutAndContactForm from "src/components/AboutAndContactForm/AboutAndContactForm.tsx";
import PropertyLivingSection from "../../components/PropertyLiving/PropertyLivingSection.tsx";
import {
  formatDescriptionParagraphs,
  splitDescriptionLines,
} from "../../util/descriptionText.ts";
import StickyPropertyCta from "src/components/StickyPropertyCta/StickyPropertyCta.tsx";
import PropertyNegotiator from "../../components/PropertyNegotiator/PropertyNegotiator.tsx";
import NeighbourhoodTab, {
  EMPTY_POIS_BY_CATEGORY,
  Poi,
  PoiCategoryKey,
} from "../../components/NeighbourhoodTab/NeighbourhoodTab.tsx";
import { fetchNearbyPois } from "../../util/fetchNearbyPois.ts";
import SimilarProperties from "../../components/SimilarProperties/SimilarProperties.tsx";
import CompareTray from "../../components/PropertyActions/CompareTray.tsx";
import PropertySeo from "../../components/Seo/PropertySeo.tsx";
import { useRecentlyViewed } from "../../hooks/useRecentlyViewed";
import { submitContact } from "../../api/contact";
import { MAPBOX_TOKEN } from "../../config/mapbox";
import { enquiryInputClassName } from "../../components/forms/EnquiryFormFields";
import {
  collectVideoEmbedUrls,
  getAvailabilityLabel,
  isEmbeddableVideoUrl,
  monthlyRentPcm,
  parseCouncilTaxFromDescription,
  parseEpcFromDescription,
  splitMediaByType,
} from "../../util/propertyParsing";
import {
  Bath,
  BedDouble,
  CalendarRange,
  KeyRound,
  LampDesk,
  PoundSterling,
  Ruler,
  Sofa,
  type LucideIcon,
  Download,
} from "lucide-react";
import { propertyBrochureUrl } from "../../api/properties";
import SafeImage from "../../components/SafeImage/SafeImage";

interface Image {
  url: string;
}

interface Property {
  id: number;
  display_address: string;
  price: number;
  rent_frequency: string;
  date_available?: string;
  minimum_term?: number | null;
  latitude: number;
  longitude: number;
  images: Image[];
  floorPlanImages: Image[];
  bedrooms: number | null;
  bathrooms: number | null;
  reception_rooms: number | null;
  internal_area: number | null;
  is_furnished: boolean;
  new_home?: boolean | null;
  available_now: boolean;
  summary: string;
  description: string[];
  property_type: string;
  features: string[];
  deposit: number | null;
  postcode: string;
  status: string | null;
  let_type: string | null;
}

type PropertyRaw = PropertyApi;

function buildStaticMapUrl(args: {
  lon: number;
  lat: number;
  token: string;
  zoom?: number;
}): string {
  // Mapbox Static Images API: https://docs.mapbox.com/api/maps/static-images/
  const { lon, lat, token, zoom = 14 } = args;

  // A simple red pin marker overlay at the property location
  const overlay = `pin-s+e11d48(${lon},${lat})`;
  const center = `${lon},${lat},${zoom},0,0`; // zoom,bearing,pitch
  const size = "900x420";
  const styleId = "mapbox/streets-v12";

  return `https://api.mapbox.com/styles/v1/${styleId}/static/${encodeURIComponent(
    overlay
  )}/${center}/${size}?access_token=${token}&logo=false&attribution=false`;
}

const transformProperty = (raw: PropertyRaw): Property => {
  const mediaSource =
    raw.photos?.length || raw.floorPlans?.length
      ? {
          photos: raw.photos ?? [],
          floorPlans: raw.floorPlans ?? [],
          documents: raw.documents ?? [],
        }
      : splitMediaByType(raw.media ?? []);

  const images: Image[] = mediaSource.photos
    .filter((m) => m.media_url && !isEmbeddableVideoUrl(m.media_url))
    .map((m) => ({ url: m.media_url }));

  const floorPlanImages: Image[] = mediaSource.floorPlans
    .filter((m) => m.media_url)
    .map((m) => ({ url: m.media_url }));

  const latitude = raw.latitude ?? 51.5074;
  const longitude = raw.longitude ?? -0.1278;

  return {
    id: raw.id,
    display_address: raw.display_address,
    price: raw.price,
    rent_frequency: raw.rent_frequency,
    date_available: raw.date_available,
    minimum_term: raw.minimum_term ?? null,
    latitude,
    longitude,
    images,
    floorPlanImages,
    bedrooms: raw.bedrooms ?? null,
    bathrooms: raw.bathrooms ?? null,
    reception_rooms: raw.reception_rooms ?? null,
    internal_area: raw.internal_area ?? null,
    is_furnished: raw.is_furnished,
    new_home: raw.new_home ?? null,
    available_now: raw.available_now,
    summary: raw.summary,
    description: raw.description ?? [],
    property_type: raw.property_type,
    features: raw.features || [],
    deposit: raw.deposit ?? null,
    postcode: raw.postcode ?? "",
    status: raw.status ?? null,
    let_type: raw.let_type ?? null,
  };
};

const SinglePropertyPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { addViewed } = useRecentlyViewed();
  const {
    items: properties,
    status,
    error,
  } = useSelector((state: RootState) => state.properties);

  useEffect(() => {
    if (status === "idle") {
      dispatch(fetchProperties());
    }
  }, [dispatch, status]);

  const rawProperty = properties.find((p) => p.id === Number(id));
  const property: Property | undefined = rawProperty
    ? transformProperty(rawProperty)
    : undefined;
  const videoEmbeds = rawProperty
    ? collectVideoEmbedUrls(
        [
          ...(rawProperty.media ?? []),
          ...(rawProperty.photos ?? []),
          ...(rawProperty.floorPlans ?? []),
          ...(rawProperty.documents ?? []),
        ],
        rawProperty.virtualTourUrl
      )
    : [];

  const enquiryFormRef = useRef<HTMLDivElement>(null);
  const [enquiryName, setEnquiryName] = useState("");
  const [enquiryEmail, setEnquiryEmail] = useState("");
  const [enquiryPhone, setEnquiryPhone] = useState("");
  const [enquiryMessage, setEnquiryMessage] = useState("");
  const [enquirySubmitting, setEnquirySubmitting] = useState(false);
  const [enquiryError, setEnquiryError] = useState<string | null>(null);
  const [enquirySuccess, setEnquirySuccess] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [poisByCategory, setPoisByCategory] =
    useState<Record<PoiCategoryKey, Poi[]>>(EMPTY_POIS_BY_CATEGORY);
  const [loadingPois, setLoadingPois] = useState(false);
  const [poisError, setPoisError] = useState("");
  const [staticMapUrl, setStaticMapUrl] = useState<string>("");
  const [staticMapLoading, setStaticMapLoading] = useState(false);
  const [staticMapError, setStaticMapError] = useState("");

  useEffect(() => {
    // reset carousel when navigating between properties
    setGalleryIndex(0);
  }, [id]);

  useEffect(() => {
    if (rawProperty?.id) {
      addViewed(rawProperty.id);
    }
  }, [rawProperty?.id, addViewed]);

  const mapboxToken = MAPBOX_TOKEN;
  const propertyId = property?.id;
  const propertyLon = property?.longitude;
  const propertyLat = property?.latitude;

  // Auto-load static map + POIs when property loads/changes
  useEffect(() => {
    if (!mapboxToken) return;
    if (propertyId == null) return;
    if (typeof propertyLon !== "number" || typeof propertyLat !== "number")
      return;

    setStaticMapError("");
    setStaticMapLoading(true);
    setStaticMapUrl(
      buildStaticMapUrl({
        lon: propertyLon,
        lat: propertyLat,
        token: mapboxToken,
        zoom: 14,
      })
    );

    const loadPois = async () => {
      setPoisError("");
      setLoadingPois(true);
      try {
        const next = await fetchNearbyPois(
          propertyLon,
          propertyLat,
          mapboxToken
        );
        setPoisByCategory(next);
      } catch (err) {
        console.error("Failed to load POIs", err);
        setPoisError("Unable to load nearby POIs.");
      } finally {
        setLoadingPois(false);
      }
    };

    loadPois();
  }, [mapboxToken, propertyId, propertyLon, propertyLat]);

  if ((status === "loading" || status === "idle") && !property) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spinner />
      </div>
    );
  }

  if (status === "failed" && !property) {
    return (
      <div className="flex items-center justify-center h-screen text-red-600">
        {error || "Failed to load property"}
      </div>
    );
  }

  if (!property) {
    return <div>Property not found.</div>;
  }

  const galleryImages =
    Array.isArray(property.images) && property.images.length > 0
      ? property.images
      : [{ url: "https://via.placeholder.com/2000x800" }];
  const safeGalleryIndex = Math.max(
    0,
    Math.min(galleryIndex, galleryImages.length - 1)
  );
  const galleryActiveUrl =
    galleryImages[safeGalleryIndex]?.url ||
    "https://via.placeholder.com/2000x800";
  const prevGallery = () =>
    setGalleryIndex(
      (prev) => (prev - 1 + galleryImages.length) % galleryImages.length
    );
  const nextGallery = () =>
    setGalleryIndex((prev) => (prev + 1) % galleryImages.length);

  const epcRating = parseEpcFromDescription(property.description);
  const councilTaxBand = parseCouncilTaxFromDescription(property.description);
  const availabilityLabel = getAvailabilityLabel(
    property.status,
    property.available_now,
    property.date_available
  );
  const pcmRent = monthlyRentPcm(property.price, property.rent_frequency);
  const descriptionParagraphs = formatDescriptionParagraphs(
    property.description.filter(
      (line) => !/^(EPC|Council\s+tax)/i.test(line.trim())
    )
  );

  const scrollToEnquiryForm = () => {
    enquiryFormRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToLivingHere = () => {
    document
      .getElementById("living-here")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  const resetEnquiryForm = () => {
    setEnquiryName("");
    setEnquiryEmail("");
    setEnquiryPhone("");
    setEnquiryMessage("");
  };

  const handlePropertyEnquiry = async (
    e: React.FormEvent,
    enquiryType: "general" | "viewing"
  ) => {
    e.preventDefault();
    if (!rawProperty) return;

    setEnquiryError(null);
    setEnquirySuccess(false);
    setEnquirySubmitting(true);

    const subjectPrefix =
      enquiryType === "viewing" ? "Book Viewing" : "Property Enquiry";

    try {
      await submitContact({
        name: enquiryName.trim(),
        email: enquiryEmail.trim(),
        phone: enquiryPhone.trim() || undefined,
        subject: `${subjectPrefix}: ${property.display_address}`,
        message: enquiryMessage.trim(),
        sourcePage: location.pathname,
        propertyId: rawProperty.id,
        mri_id: rawProperty.mri_id,
        enquiryType,
      });

      setEnquirySuccess(true);
      resetEnquiryForm();
    } catch (err) {
      setEnquiryError(
        err instanceof Error ? err.message : "Failed to send your enquiry"
      );
    } finally {
      setEnquirySubmitting(false);
    }
  };

  // Defensive property images logic
  const mainImage =
    Array.isArray(property.images) && property.images.length > 0
      ? property.images[0]?.url || "https://via.placeholder.com/2000x800"
      : "https://via.placeholder.com/2000x800";

  // Main POI button: category-based POIs (coffee/food/shop/police)
  // (POIs + static map are auto-loaded on page load; no manual buttons needed)

  const handleOpenInMapView = () => {
    navigate(`/services/rent/properties/map?focusId=${property.id}`);
  };

  // Defensive summary splitting
  const summaryParagraphs =
    typeof property.summary === "string"
      ? splitDescriptionLines(property.summary.split("<br><br>"))
      : [];

  // Defensive feature mapping
  const featuresArray =
    Array.isArray(property.features) && property.features.length > 0
      ? property.features
      : [];

  const keyDetailItems: {
    label: string;
    value: string;
    icon: LucideIcon;
  }[] = [
    {
      label: "Bedrooms",
      value:
        typeof property.bedrooms === "number" ? String(property.bedrooms) : "—",
      icon: BedDouble,
    },
    {
      label: "Bathrooms",
      value:
        typeof property.bathrooms === "number"
          ? String(property.bathrooms)
          : "—",
      icon: Bath,
    },
    {
      label: "Reception",
      value:
        typeof property.reception_rooms === "number"
          ? String(property.reception_rooms)
          : "—",
      icon: Sofa,
    },
    {
      label: "Sq ft",
      value: property.internal_area
        ? property.internal_area.toLocaleString()
        : "—",
      icon: Ruler,
    },
    {
      label: "Deposit",
      value:
        property.deposit != null
          ? `£${property.deposit.toLocaleString()}`
          : "—",
      icon: PoundSterling,
    },
    {
      label: "Furnished",
      value: property.is_furnished ? "Yes" : "No",
      icon: LampDesk,
    },
    {
      label: "Let type",
      value: property.let_type || "—",
      icon: KeyRound,
    },
    {
      label: "Min term",
      value: property.minimum_term ? `${property.minimum_term} mo` : "—",
      icon: CalendarRange,
    },
  ];

  return (
    <>
      {rawProperty ? (
        <PropertySeo property={rawProperty} imageUrl={mainImage} />
      ) : null}
      <div className="pb-20 lg:pb-0">
      {/* HERO GALLERY */}
      <section className="relative h-[85vh] w-full overflow-hidden clip-path-[polygon(0_0,100%_0,100%_90%,0%_100%)]">
        <SafeImage
          src={mainImage}
          className="w-full h-full object-cover animate-zoomIn"
          alt={property.display_address}
        />
        <div className="absolute inset-0 bg-black/30 z-0"></div>
        <div className="absolute bottom-0 left-0 w-full h-1/2 bg-[linear-gradient(to_top,rgba(0,0,0,0.8),transparent)] flex flex-col justify-end p-16 pb-32 text-white">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="bg-white text-text-obsidian inline-block py-1 px-4 font-teko text-lg transform skew-x-[var(--skew)] w-fit">
              <span className="block transform skew-x-[10deg]">
                {availabilityLabel}
              </span>
            </div>
            {epcRating ? (
              <div className="bg-brand-yellow text-text-obsidian inline-block py-1 px-4 font-teko text-lg transform skew-x-[var(--skew)] w-fit">
                <span className="block transform skew-x-[10deg]">
                  EPC {epcRating}
                </span>
              </div>
            ) : null}
          </div>
          <h1 className="page-hero-title text-shadow-[0_10px_30px_rgba(0,0,0,0.5)]">
            {property.display_address}
          </h1>
          <span className="font-manrope text-lg opacity-90 uppercase tracking-wider">
            {property.property_type}
            {property.bedrooms ? ` • ${property.bedrooms} Bed` : ""}
            {property.bathrooms ? ` • ${property.bathrooms} Bath` : ""}
          </span>
          <p className="font-teko text-4xl text-white mt-4 rounded font-light">
            £{pcmRent.toLocaleString()} PCM
            {property.rent_frequency !== "Monthly" ? (
              <span className="block font-manrope text-base opacity-80 mt-1">
                Listed at £{property.price?.toLocaleString()}{" "}
                {property.rent_frequency}
              </span>
            ) : null}
          </p>
        </div>
        <div className="absolute bottom-5 right-5 z-50 flex items-center gap-2">
          <a
            href={propertyBrochureUrl(property.id)}
            aria-disabled={!rawProperty?.brochure_available}
            onClick={(event) => {
              if (!rawProperty?.brochure_available) event.preventDefault();
            }}
            className={`inline-flex items-center gap-2 px-4 py-2 border border-solid font-teko text-sm uppercase transition-all duration-300 ${
              rawProperty?.brochure_available
                ? "bg-white text-text-obsidian border-white hover:bg-brand-yellow hover:border-brand-yellow"
                : "bg-white/60 text-text-slate border-white/60 cursor-not-allowed"
            }`}
            title={
              rawProperty?.brochure_available
                ? "Download property brochure PDF"
                : "Brochure is being prepared"
            }
          >
            <Download size={15} aria-hidden="true" />
            Brochure PDF
          </a>
          <button
            type="button"
            onClick={scrollToLivingHere}
            className="px-4 py-2 bg-transparent text-white border border-white font-teko text-sm uppercase cursor-pointer transition-all duration-300 hover:bg-white hover:text-text-obsidian"
          >
            Plan your day
          </button>
        </div>
      </section>

      {/* MAIN LAYOUT */}
      <div className="grid lg:grid-cols-3 grid-cols-1 gap-12 max-w-[1600px] mx-auto px-[5%] py-10 ">
        {/* LEFT COL: SPECS & DETAILS */}
        <div className="col-span-2 flex flex-col gap-16">
          <section>
            <h2 className="section-title">Key Details</h2>
            <div className="section-block bg-bg-white border border-solid border-[rgba(0,0,0,0.05)] shadow-soft p-5 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 pb-5 mb-5 border-b border-solid border-[rgba(0,0,0,0.06)]">
                <div>
                  <p className="font-manrope text-xs uppercase tracking-wider text-text-slate">
                    Monthly rent
                  </p>
                  <p className="font-teko text-3xl text-text-obsidian leading-tight mt-1">
                    £{property.price?.toLocaleString() || "—"}
                    <span className="text-brand-yellow text-xl ml-2">
                      {property.rent_frequency === "Monthly"
                        ? "PCM"
                        : property.rent_frequency || ""}
                    </span>
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <span className="text-[11px] font-manrope uppercase tracking-wider bg-bg-porcelain px-2.5 py-1 border border-solid border-[rgba(0,0,0,0.08)] text-text-slate">
                    {property.property_type || "Type —"}
                  </span>
                  <span className="text-[11px] font-manrope uppercase tracking-wider bg-bg-porcelain px-2.5 py-1 border border-solid border-[rgba(0,0,0,0.08)] text-text-slate">
                    {property.status || "Available"}
                  </span>
                  {property.postcode ? (
                    <span className="text-[11px] font-manrope uppercase tracking-wider bg-bg-porcelain px-2.5 py-1 border border-solid border-[rgba(0,0,0,0.08)] text-text-slate">
                      {property.postcode}
                    </span>
                  ) : null}
                </div>
              </div>
              <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-5 sm:gap-x-6 sm:gap-y-6">
                {keyDetailItems.map((item) => {
                  const DetailIcon = item.icon;
                  return (
                    <div
                      key={item.label}
                      className="flex items-center gap-3 min-w-0"
                    >
                      <div
                        className="shrink-0 w-10 h-10 rounded-full bg-brand-yellow/12 flex items-center justify-center"
                        aria-hidden
                      >
                        <DetailIcon
                          className="w-[18px] h-[18px] text-brand-yellow"
                          strokeWidth={1.75}
                        />
                      </div>
                      <div className="min-w-0">
                        <dt className="font-manrope text-[10px] uppercase tracking-wider text-text-slate leading-tight">
                          {item.label}
                        </dt>
                        <dd className="font-teko text-xl sm:text-2xl text-text-obsidian leading-tight mt-0.5 truncate">
                          {item.value}
                        </dd>
                      </div>
                    </div>
                  );
                })}
              </dl>
            </div>
          </section>

          <section>
            <h2 className="section-title">Gallery</h2>
            <div className="section-block bg-bg-white border border-solid border-[rgba(0,0,0,0.05)] shadow-soft overflow-hidden">
              <div className="relative h-[420px] w-full bg-black">
                <SafeImage
                  src={galleryActiveUrl}
                  alt="Property gallery"
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={prevGallery}
                  className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/85 text-text-obsidian w-10 h-10 rounded-full flex items-center justify-center hover:bg-brand-yellow transition"
                  aria-label="Previous image"
                >
                  ‹
                </button>
                <button
                  type="button"
                  onClick={nextGallery}
                  className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/85 text-text-obsidian w-10 h-10 rounded-full flex items-center justify-center hover:bg-brand-yellow transition"
                  aria-label="Next image"
                >
                  ›
                </button>
                <div className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded">
                  {safeGalleryIndex + 1} / {galleryImages.length}
                </div>
              </div>

              <div className="p-4">
                <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
                  {galleryImages.map((img, idx) => (
                    <button
                      key={`${img.url}-${idx}`}
                      type="button"
                      onClick={() => setGalleryIndex(idx)}
                      className={`relative h-16 overflow-hidden border border-solid transition ${
                        idx === safeGalleryIndex
                          ? "border-brand-yellow"
                          : "border-[rgba(0,0,0,0.12)] hover:border-text-obsidian"
                      }`}
                      aria-label={`View image ${idx + 1}`}
                    >
                      <SafeImage
                        src={img.url}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <PropertyLivingSection
            property={{
              id: property.id,
              price: property.price,
              rent_frequency: property.rent_frequency,
              deposit: property.deposit,
              postcode: property.postcode,
              latitude: property.latitude,
              longitude: property.longitude,
              bedrooms: property.bedrooms,
              internal_area: property.internal_area,
              description: property.description,
            }}
          />

          {videoEmbeds.length > 0 ? (
            <section>
              <h2 className="section-title">Virtual Tour</h2>
              <div className="section-block bg-bg-white border border-solid border-[rgba(0,0,0,0.05)] shadow-soft overflow-hidden space-y-4 p-4">
                {videoEmbeds.map((embedUrl) => (
                  <div
                    key={embedUrl}
                    className="relative w-full aspect-video bg-black"
                  >
                    <iframe
                      src={embedUrl}
                      title="Property virtual tour"
                      className="absolute inset-0 w-full h-full border-0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                      allowFullScreen
                    />
                  </div>
                ))}
              </div>
            </section>
          ) : null}
          {property.floorPlanImages.length > 0 ? (
            <section>
              <h2 className="section-title">Floor Plan</h2>
              <div className="section-block bg-[#1a1a1a] p-6 relative clip-path-[polygon(0_0,100%_0,100%_95%,95%_100%,0_100%)]">
                <SafeImage
                  src={property.floorPlanImages[0].url}
                  className="w-full max-h-[520px] object-contain filter invert opacity-90 mix-blend-screen"
                  alt="Floor plan"
                />
              </div>
            </section>
          ) : null}

          {(epcRating || councilTaxBand) ? (
            <section>
              <h2 className="section-title">Energy &amp; Council Tax</h2>
              <div className="section-block bg-bg-white border border-solid border-[rgba(0,0,0,0.05)] shadow-soft p-6 flex flex-wrap gap-6">
                {epcRating ? (
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-brand-yellow text-text-obsidian font-teko text-3xl flex items-center justify-center">
                      {epcRating}
                    </div>
                    <div>
                      <div className="font-teko text-2xl text-text-obsidian">
                        EPC Rating
                      </div>
                      <div className="font-manrope text-sm text-text-slate">
                        Energy Performance Certificate
                      </div>
                    </div>
                  </div>
                ) : null}
                {councilTaxBand ? (
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-text-obsidian text-white font-teko text-3xl flex items-center justify-center">
                      {councilTaxBand}
                    </div>
                    <div>
                      <div className="font-teko text-2xl text-text-obsidian">
                        Council Tax Band
                      </div>
                      <div className="font-manrope text-sm text-text-slate">
                        Annual council tax banding
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </section>
          ) : null}

          {descriptionParagraphs.length > 0 ? (
            <section>
              <h2 className="section-title">Full Description</h2>
              <div className="section-block bg-bg-white border border-solid border-[rgba(0,0,0,0.05)] shadow-soft p-6 md:p-8">
                <div className="property-description">
                  {descriptionParagraphs.map((paragraph, index) => (
                    <p
                      key={index}
                      className={
                        index === 0
                          ? "property-description-lead"
                          : undefined
                      }
                    >
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            </section>
          ) : null}

          {/* 2. Description */}
          <section>
            <h2 className="section-title">Property Brief</h2>
            <div className="section-block bg-bg-white border border-solid border-[rgba(0,0,0,0.05)] shadow-soft p-6 md:p-8">
              <div className="property-description">
                {(summaryParagraphs.length > 0
                  ? summaryParagraphs
                  : [property.summary]
                ).map((paragraph: string, index: number) => (
                  <p
                    key={index}
                    className={
                      index === 0 ? "property-description-lead" : undefined
                    }
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
          </section>
          {featuresArray.length > 0 ? (
            <section>
              <h2 className="section-title">Property Features</h2>
              <div className="section-block bg-bg-white border border-solid border-[rgba(0,0,0,0.05)] shadow-soft p-6">
                <div className="grid md:grid-cols-2 gap-3 grid-cols-1">
                  {featuresArray.map((feature: string, index: number) => (
                    <div
                      key={index}
                      className="flex items-center gap-4 p-4 border-b border-b-solid border-b-[rgba(0,0,0,0.05)]"
                    >
                      <span className="text-brand-yellow text-xl">✓</span>
                      <span className="font-medium text-base">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          ) : null}
          {/* 4. Blueprint */}
          {/* <section>
            <h2 className="section-title">
              Schematics
            </h2>
            <div className="bg-[#1a1a1a] p-12 relative clip-path-[polygon(0_0,100%_0,100%_95%,95%_100%,0_100%)]">
              <span className="absolute top-8 left-8 text-brand-yellow border border-solid border-brand-yellow py-1 px-4 font-teko text-lg uppercase">
                Floor {floorNumber} Layout
              </span>
              <SafeImage
                src="https://images.services.expertagent.co.uk/services/Media/ea75c93d-4c3e-42c2-840e-7493a774776e/1.png"
                className="w-full filter invert opacity-90 mix-blend-screen"
                alt="Floorplan"
              />
            </div>
          </section> */}
          <section>
            <h2 className="section-title">Neighbourhood</h2>
            <div className="section-block">
            <NeighbourhoodTab
              postcode={property.postcode}
              displayAddress={property.display_address}
              staticMapUrl={staticMapUrl}
              staticMapLoading={staticMapLoading}
              staticMapError={staticMapError}
              onMapClick={handleOpenInMapView}
              onMapLoad={() => setStaticMapLoading(false)}
              onMapError={() => {
                setStaticMapLoading(false);
                setStaticMapError("Failed to load static map image.");
              }}
              poisByCategory={poisByCategory}
              loadingPois={loadingPois}
              poisError={poisError}
            />
            </div>
          </section>
        </div>

        {/* RIGHT COL: STICKY CONTROL PANEL */}
        <div className="relative" ref={enquiryFormRef} id="property-enquiry-form">
          <div className="sticky top-28 max-h-[calc(100vh-7rem)] overflow-y-auto">
            <div className="bg-[var(--glass-surface)] backdrop-blur-[25px] border-[var(--glass-border)] p-6 shadow-[0_30px_60px_rgba(0,0,0,0.05)]">
              <div className="border-b border-b-solid border-b-[rgba(0,0,0,0.05)] pb-4 mb-5">
                <span className="font-manrope text-xs uppercase tracking-wider text-text-slate block mb-2">
                  Enquire about this property
                </span>
                <div className="font-teko text-2xl text-text-obsidian leading-tight">
                  £{Number(property.price).toLocaleString()}
                  <span className="font-manrope text-xs text-text-slate ml-2 uppercase tracking-wider">
                    PCM
                  </span>
                </div>
              </div>
              <form
                className="grid gap-3"
                onSubmit={(e) => handlePropertyEnquiry(e, "general")}
              >
                {enquirySuccess && (
                  <p className="font-manrope text-sm text-green-700 bg-green-50 p-4 border border-solid border-green-200">
                    Thank you — your enquiry has been sent.
                  </p>
                )}
                {enquiryError && (
                  <p className="font-manrope text-sm text-red-700 bg-red-50 p-4 border border-solid border-red-200">
                    {enquiryError}
                  </p>
                )}
                <input
                  type="text"
                  className={enquiryInputClassName}
                  placeholder="Full Name"
                  id="agentFullNameInput"
                  value={enquiryName}
                  onChange={(e) => setEnquiryName(e.target.value)}
                  required
                  disabled={enquirySubmitting}
                />
                <input
                  type="email"
                  className={enquiryInputClassName}
                  placeholder="Email Address"
                  id="agentEmailAddressInput"
                  value={enquiryEmail}
                  onChange={(e) => setEnquiryEmail(e.target.value)}
                  required
                  disabled={enquirySubmitting}
                />
                <input
                  type="tel"
                  className={enquiryInputClassName}
                  placeholder="Phone Number"
                  id="agentPhoneNumberInput"
                  value={enquiryPhone}
                  onChange={(e) => setEnquiryPhone(e.target.value)}
                  disabled={enquirySubmitting}
                />
                <textarea
                  className={enquiryInputClassName}
                  rows={3}
                  placeholder="I am interested in this property..."
                  id="agentMessageInput"
                  value={enquiryMessage}
                  onChange={(e) => setEnquiryMessage(e.target.value)}
                  required
                  disabled={enquirySubmitting}
                ></textarea>
                <button
                  className="w-full p-3 mt-2 bg-text-obsidian text-white border-none font-teko text-base uppercase cursor-pointer transition-all duration-300 hover:bg-brand-yellow hover:text-black disabled:opacity-60 disabled:cursor-not-allowed"
                  type="submit"
                  disabled={enquirySubmitting}
                >
                  {enquirySubmitting ? "Sending…" : "Send enquiry"}
                </button>
                <button
                  className="w-full p-3 bg-transparent border border-solid border-text-obsidian font-teko text-base uppercase cursor-pointer transition-all duration-300 hover:bg-white disabled:opacity-60 disabled:cursor-not-allowed"
                  type="button"
                  disabled={enquirySubmitting}
                  onClick={(e) => handlePropertyEnquiry(e, "viewing")}
                >
                  {enquirySubmitting ? "Sending…" : "Book Viewing"}
                </button>
              </form>
              {property.postcode ? (
                <PropertyNegotiator postcode={property.postcode} />
              ) : null}
            </div>
          </div>
        </div>
      </div>
      </div>

      <StickyPropertyCta onEnquire={scrollToEnquiryForm} />
      <CompareTray />

      {rawProperty ? <SimilarProperties property={rawProperty} /> : null}

      <TenantGuides />
      <AreaGuideTeaser />
      <AboutAndContactForm
        sourcePage={location.pathname}
        defaultSubject={property?.display_address}
      />
    </>
  );
};

export default SinglePropertyPage;
